import type { AppNotification } from '../../api/types';

/** Where a notification leads. Links are written by the notifications service as /?files etc. */
export function notificationTarget(notification: AppNotification, fallbackTenant: string | undefined): string {
  const tenant = notification.tenantId ?? fallbackTenant;
  const link = notification.link ?? '';
  if (link.startsWith('/?invitations')) return '/?invitations';
  if (!tenant) return '/';
  if (link.startsWith('/?members')) return `/c/${tenant}/settings/members`;
  if (link.startsWith('/?privacy')) return `/c/${tenant}/settings/privacy`;
  if (link.startsWith('/?automation')) return `/c/${tenant}/settings/automation`;
  if (notification.type.startsWith('signing.')) return `/c/${tenant}/signing`;
  if (notification.type.startsWith('share.')) return '/shared';
  if (link.startsWith('/?imports')) {
    // An import that has finished (TF-164): the imports page, with it marked.
    const id = new URLSearchParams(link.slice(2)).get('import');
    return `/c/${tenant}/crm/imports${id ? `?import=${encodeURIComponent(id)}` : ''}`;
  }
  if (link.startsWith('/?deals')) {
    // The demo follow-up (TF-242): a reminder opens its deal, the weekly digest the deals.
    const deal = new URLSearchParams(link.slice(2)).get('deal');
    return deal ? `/c/${tenant}/crm/deals/${encodeURIComponent(deal)}` : `/c/${tenant}/crm/deals`;
  }
  if (link.startsWith('/?contacts')) {
    // A website form's enquiry (TF-243): the contact it made or found.
    const contact = new URLSearchParams(link.slice(2)).get('contact');
    return contact ? `/c/${tenant}/crm/contacts/${encodeURIComponent(contact)}` : `/c/${tenant}/crm/contacts`;
  }
  if (link.startsWith('/?files')) {
    // A team's document (TF-301): the document; else the drive.
    const file = new URLSearchParams(link.slice(2)).get('file');
    return file ? `/c/${tenant}/file/${encodeURIComponent(file)}` : `/c/${tenant}/files`;
  }
  if (link.startsWith('/?teams')) {
    // Somebody joined or left a team I run (TF-301).
    const team = new URLSearchParams(link.slice(2)).get('team');
    return team ? `/c/${tenant}/settings/teams/${encodeURIComponent(team)}` : `/c/${tenant}/settings/teams`;
  }
  if (link.startsWith('/?tasks')) {
    // A task's own notification names the task: My tasks opens with it in the quick view (TF-17).
    const task = new URLSearchParams(link.slice(2)).get('task');
    return `/c/${tenant}/crm/tasks${task ? `?task=${encodeURIComponent(task)}` : ''}`;
  }
  return `/c/${tenant}/files`;
}
